'use client';

import { useState, type FC } from 'react';

import { Button } from '@wwe-portal/ui/components/button';
import { ComingSoonImg } from '@wwe-portal/ui/components/icons/comminh-soon';
import { Each } from '@wwe-portal/ui/components/utils';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';

import type { ExclusiveItemProps } from './exclusive-item';
import {
  ExclusiveItemDesktop,
  ExclusiveItemDesktopSkeleton,
  ExclusiveItemMobile,
  ExclusiveItemMobileSkeleton,
} from './exclusive-item';

export interface ExclusiveItemCollectionProps {
  isLoading: boolean;
  data: ExclusiveItemProps[];
  onClick: (offer: ExclusiveItemProps) => void;
  onShowOfferCard: (offer: ExclusiveItemProps) => void;
  processingOfferId: string | undefined;
}

const LOAD_MORE_STEP = 3;

const MobileCollection: FC<ExclusiveItemCollectionProps> = ({
  isLoading,
  data,
  onClick,
  onShowOfferCard,
  processingOfferId,
}) => {
  const [loadMore, setLoadMore] = useState(LOAD_MORE_STEP);

  if (isLoading)
    return (
      <div className="flex flex-col flex-wrap justify-start gap-y-[10px]">
        <Each
          data={new Array(5).fill(0)}
          render={(_, index) => <ExclusiveItemMobileSkeleton key={index} />}
        />
      </div>
    );
  return (
    <>
      {data.length ? (
        <div className="flex flex-col flex-wrap justify-start gap-y-[10px]">
          <Each
            data={data.slice(0, loadMore)}
            render={(item: ExclusiveItemProps) => (
              <ExclusiveItemMobile
                exclusiveItem={item}
                isLoading={processingOfferId === item.offerData.id}
                key={item.id}
                onClick={onClick}
                onShowOfferCard={onShowOfferCard}
              />
            )}
          />
          {Boolean(data.length) && loadMore < data.length && (
            <div className="flex w-full justify-center pt-3 md:pt-5">
              <Button
                className="h-[44px] w-full font-semibold md:w-[200px]"
                onClick={() => setLoadMore(loadMore + 3)}
                variant="secondary"
              >
                View More
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-back300 flex h-[129px] w-full items-center justify-center rounded-md border-4">
          <ComingSoonImg />
        </div>
      )}
    </>
  );
};

const DesktopCollectionSkeleton = () => {
  const { xl3 } = useResponsive();
  const getNoItems = () => {
    if (xl3) return 5;
    return 6;
  };
  return (
    <div className="3xl:grid-cols-5 grid grid-cols-2 gap-[1.875rem] 2xl:grid-cols-3">
      <Each
        data={new Array(getNoItems()).fill(0)}
        render={(_, index) => <ExclusiveItemDesktopSkeleton key={index} />}
      />
    </div>
  );
};

const DesktopCollection: FC<
  Omit<ExclusiveItemCollectionProps, 'onShowOfferCard'>
> = ({ isLoading, data, onClick, processingOfferId }) => {
  if (isLoading) {
    return <DesktopCollectionSkeleton />;
  }
  return (
    <>
      {data.length ? (
        <div className="3xl:grid-cols-5 grid grid-cols-2 gap-[1.875rem] 2xl:grid-cols-3">
          <Each
            data={data}
            render={(item: ExclusiveItemProps) => (
              <ExclusiveItemDesktop
                exclusiveItem={item}
                isLoading={processingOfferId === item.offerData.id}
                key={item.id}
                onClick={onClick}
              />
            )}
          />
        </div>
      ) : (
        <div className="bg-back300 flex h-[129px] w-full items-center justify-center rounded-md border-4">
          <ComingSoonImg />
        </div>
      )}
    </>
  );
};

export const ResponsiveExclusiveItemColection: FC<
  ExclusiveItemCollectionProps
> = (props) => {
  const { isMobile } = useResponsive();
  const Collection = isMobile ? MobileCollection : DesktopCollection;
  return <Collection {...props} />;
};
